// UpdatedWysiwygEditor.tsx - Enhanced editor with file upload support

import React, { useEffect, useRef, useState } from 'react';
import EnhancedLinkDialog from './EnhancedLinkDialog';
import { ClientFirebaseConfig } from '../client/clientStorageService';
import "./WysiwygStyles.css";

interface WysiwygEditorProps {
  initialHtml: string;
  onChange: (html: string) => void;
  clientStorageConfig?: ClientFirebaseConfig; // Optional client storage config
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({ 
  initialHtml, 
  onChange, 
  clientStorageConfig 
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  const [htmlInput, setHtmlInput] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [savedSelection, setSavedSelection] = useState<Range | null>(null);
  
  // Format HTML for editor display - add spacing between consecutive elements
  const formatHtmlForDisplay = (html: string): string => {
    if (!html) return '';
    
    try {
      // Create a temporary element to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Find adjacent elements that need spacing between them
      const processAdjacentElements = (parentNode: Element) => {
        // Process all children first
        Array.from(parentNode.children).forEach(child => {
          processAdjacentElements(child);
        });
        
        // Get all children as an array for easier processing
        const children = Array.from(parentNode.childNodes);
        
        // Add spacing between consecutive elements like links, bold text, etc.
        for (let i = children.length - 1; i > 0; i--) {
          const currentNode = children[i];
          const prevNode = children[i-1];
          
          // Check if these are elements that should have spacing
          if (shouldAddSpacingBetween(prevNode, currentNode)) {
            // Insert line break between elements
            parentNode.insertBefore(document.createElement('br'), currentNode);
          }
        }
      };
      
      // Determine if two nodes should have spacing between them
      const shouldAddSpacingBetween = (node1: Node, node2: Node): boolean => {
        // Check for consecutive formatting or link elements
        const isFormattingOrLink = (node: Node): boolean => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          
          const tagName = (node as Element).tagName.toUpperCase();
          return ['B', 'STRONG', 'I', 'EM', 'U', 'A'].includes(tagName);
        };
        
        // Check if both are formatting/link elements
        return isFormattingOrLink(node1) && isFormattingOrLink(node2);
      };
      
      // Process the HTML to add spacing
      processAdjacentElements(tempDiv);
      
      return tempDiv.innerHTML;
    } catch (err) {
      console.warn('Error formatting HTML for display:', err);
      return html; // Return original if error
    }
  };
  
  // Initialize the editor when component mounts
  useEffect(() => {
    if (editorRef.current) {
      // Set initial content with proper formatting for display
      const formattedHtml = formatHtmlForDisplay(initialHtml || '');
      editorRef.current.innerHTML = formattedHtml;
      
      // Add event listener for input
      const handleInput = () => {
        if (editorRef.current) {
          onChange(editorRef.current.innerHTML);
        }
      };
      
      // Create a keydown handler to limit consecutive enters
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          // Check current content for consecutive breaks
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const startNode = range.startContainer;
            
            // Count previous br elements or empty paragraphs
            let consecutiveBreaks = 0;
            let node = startNode;
            
            // Simple check for consecutive breaks by looking at previous siblings
            if (node.nodeType === 3 && node.textContent === '') { // Text node
              let prevNode = node.previousSibling;
              while (prevNode) {
                if (prevNode.nodeName === 'BR' || 
                    (prevNode.nodeName === 'DIV' && (!prevNode.textContent || prevNode.textContent.trim() === ''))) {
                  consecutiveBreaks++;
                } else if (prevNode.textContent && prevNode.textContent.trim() !== '') {
                  break;
                }
                prevNode = prevNode.previousSibling;
              }
            }
            
            // If we already have 3 breaks, prevent default
            if (consecutiveBreaks >= 3) {
              e.preventDefault();
            }
          }
        }
      };
      
      editorRef.current.addEventListener('input', handleInput);
      editorRef.current.addEventListener('blur', handleInput);
      editorRef.current.addEventListener('keydown', handleKeyDown);
      
      // Mark as initialized
      setIsInitialized(true);
      
      // Clean up event listeners on unmount
      return () => {
        if (editorRef.current) {
          editorRef.current.removeEventListener('input', handleInput);
          editorRef.current.removeEventListener('blur', handleInput);
          editorRef.current.removeEventListener('keydown', handleKeyDown);
        }
      };
    }
  }, [initialHtml]);
  
  // Update editor content when initialHtml prop changes
  useEffect(() => {
    // Only update if the editor already exists and initialHtml changes
    if (editorRef.current && isInitialized) {
      console.log("Updating editor content with:", initialHtml);
      
      // Format the HTML for display with proper spacing
      const formattedHtml = formatHtmlForDisplay(initialHtml || '');
      
      // Only update the content if it's different to avoid cursor jumps
      if (editorRef.current.innerHTML !== formattedHtml) {
        editorRef.current.innerHTML = formattedHtml;
      }
    }
  }, [initialHtml, isInitialized]);
  
  // Apply formatting command
  const execCommand = (command: string, value: string = '') => {
    if (editorRef.current) {
      // Focus the editor first
      editorRef.current.focus();
      
      // Execute the command
      document.execCommand(command, false, value);
    }
  };
  
  // Create link with enhanced dialog
  const createLink = () => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        // Save the current selection
        setSavedSelection(selection.getRangeAt(0).cloneRange());
        setShowLinkDialog(true);
      } else {
        alert('Please select text to create a link');
      }
    }
  };

  // Handle link insertion from the dialog
  const handleLinkInsert = (url: string) => {
    if (savedSelection && editorRef.current) {
      // Restore the selection
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelection);
        
        // Create the link
        execCommand('createLink', url);
        
        // Notify about the change
        onChange(editorRef.current.innerHTML);
      }
    }
    
    setShowLinkDialog(false);
    setSavedSelection(null);
  };

  // Handle link dialog cancel
  const handleLinkCancel = () => {
    setShowLinkDialog(false);
    setSavedSelection(null);
  };
  
  // Insert unordered list
  const insertList = () => {
    execCommand('insertUnorderedList');
  };
  
  // Store cursor position when opening the HTML modal
  const [savedHtmlSelection, setSavedHtmlSelection] = useState<{start: Node, startOffset: number, end: Node, endOffset: number} | null>(null);
  
  // Save selection when opening the HTML modal
  const openHtmlModal = () => {
    // Save the current selection
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Save current selection position
        setSavedHtmlSelection({
          start: range.startContainer,
          startOffset: range.startOffset,
          end: range.endContainer,
          endOffset: range.endOffset
        });
        
        // If text is selected, put it in the HTML input
        const fragment = range.cloneContents();
        const div = document.createElement('div');
        div.appendChild(fragment);
        setHtmlInput(div.innerHTML);
      } else {
        setSavedHtmlSelection(null);
        setHtmlInput('');
      }
    }
    
    setShowHtmlModal(true);
  };
  
  // Insert HTML into the editor
  const insertHtml = () => {
    if (editorRef.current && htmlInput) {
      // Focus the editor
      editorRef.current.focus();
      
      // Format the HTML for proper display before inserting
      const formattedHtml = formatHtmlForDisplay(htmlInput);
      
      // Create a temporary element with the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formattedHtml;
      
      // Get the document selection
      const selection = window.getSelection();
      
      // If we have a saved selection, restore it
      if (savedHtmlSelection && selection) {
        try {
          // Create a new range
          const range = document.createRange();
          
          // Set the range to our saved selection
          range.setStart(savedHtmlSelection.start, savedHtmlSelection.startOffset);
          range.setEnd(savedHtmlSelection.end, savedHtmlSelection.endOffset);
          
          // Apply the range to the selection
          selection.removeAllRanges();
          selection.addRange(range);
          
          // Delete any selected content
          range.deleteContents();
          
          // Insert the new HTML content
          const fragment = document.createDocumentFragment();
          
          // Add each child of the temp div to the fragment
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }
          
          // Insert at the current position
          range.insertNode(fragment);
          
          // Move cursor to the end of the inserted content
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          
          // Notify about the change
          if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
          }
        } catch (e) {
          console.error('Error restoring selection:', e);
          // Fallback to default insertion at the end
          editorRef.current.innerHTML += formattedHtml;
          onChange(editorRef.current.innerHTML);
        }
      } else {
        // Fallback: if no saved selection, append to the end
        editorRef.current.innerHTML += formattedHtml;
        onChange(editorRef.current.innerHTML);
      }
      
      // Close the modal
      setShowHtmlModal(false);
    }
  };
  
  return (
    <div className="wysiwyg-editor">
      <div className="editor-toolbar">
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('italic')}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('underline')}
          title="Underline"
        >
          <u>U</u>
        </button>
        
        <div className="toolbar-divider"></div>
        
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('formatBlock', '<p>')}
          title="Paragraph"
        >
          P
        </button>
        
        <div className="toolbar-divider"></div>
        
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={insertList}
          title="Bullet List"
        >
          • List
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={createLink}
          title="Insert Link"
        >
          Link
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={openHtmlModal}
          title="Insert HTML"
        >
          HTML
        </button>
      </div>
      
      <div
        ref={editorRef}
        className="editor-content"
        contentEditable={true}
        suppressContentEditableWarning={true}
      />
      
      <div className="editor-hints">
        <ul>
          <li>Select text and click formatting buttons to apply styles</li>
          <li>To add a link, select text first</li>
          <li>Use the HTML button to insert custom HTML code</li>
          <li>You can add at most 3 blank lines in a row</li>
          {clientStorageConfig && (
            <li>📁 Upload files directly when creating links</li>
          )}
        </ul>
      </div>
      
      {/* Enhanced Link Dialog */}
      <EnhancedLinkDialog
        isOpen={showLinkDialog}
        onInsert={handleLinkInsert}
        onCancel={handleLinkCancel}
        clientStorageConfig={clientStorageConfig}
      />
      
      {/* HTML Modal */}
      {showHtmlModal && (
        <div className="html-modal-overlay">
          <div className="html-modal">
            <div className="html-modal-header">
              <h3>Insert HTML</h3>
              <button 
                type="button" 
                className="close-modal-btn" 
                onClick={() => setShowHtmlModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="html-modal-body">
              <textarea 
                value={htmlInput} 
                onChange={(e) => setHtmlInput(e.target.value)}
                placeholder="Enter HTML code here..."
                rows={10}
              />
            </div>
            <div className="html-modal-footer">
              <button 
                type="button" 
                className="insert-html-btn" 
                onClick={insertHtml}
              >
                Insert
              </button>
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={() => setShowHtmlModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WysiwygEditor;